import React, { useState,useEffect } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, BarChart2, Layers, FileText, RefreshCcw } from 'lucide-react';

export default function AcademicSystem({ 
  accent = "#115e32",
  pageTitle = "Academic System | University of Creative Technology Chittagong",
}) {
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
  const [showMore, setShowMore] = useState(false);

  const grading = [
    { sl: 1, marks: '80% or above', grade: 'A+', gp: '4.00', remark: 'Excellent' },
    { sl: 2, marks: '75% to less than 80%', grade: 'A', gp: '3.75', remark: 'Very Good' },
    { sl: 3, marks: '70% to less than 75%', grade: 'A-', gp: '3.50', remark: '' },
    { sl: 4, marks: '65% to less than 70%', grade: 'B+', gp: '3.25', remark: 'Good' },
    { sl: 5, marks: '60% to less than 65%', grade: 'B', gp: '3.00', remark: '' },
    { sl: 6, marks: '55% to less than 60%', grade: 'B-', gp: '2.75', remark: 'Satisfactory' },
    { sl: 7, marks: '50% to less than 55%', grade: 'C+', gp: '2.50', remark: '' },
    { sl: 8, marks: '45% to less than 50%', grade: 'C', gp: '2.25', remark: 'Pass' },
    { sl: 9, marks: '40% to less than 45%', grade: 'D', gp: '2.00', remark: '' },
    { sl: 10, marks: 'less than 40%', grade: 'F', gp: '0.00', remark: 'Fail' }
  ];

  const evalScheme = [
    { label: 'Attendance', value: '10%' },
    { label: 'Class Test & Assignments', value: '15%' },
    { label: 'Viva', value: '5%' },
    { label: 'Midterm Exam', value: '30%' },
    { label: 'Final Examination', value: '40%' }
  ];

  const workloadArts = [
    { range: '3.75 – 4.00', max: '24 Cr. Hrs.' },
    { range: '3.50 – 3.74', max: '20-22 Cr. Hrs.' },
    { range: '2.75 – 3.49', max: '18-20 Cr. Hrs.' },
    { range: '2.25 – 2.74', max: '15-18 Cr. Hrs.' },
    { range: '2.00 – 2.24', max: '12-15 Cr. Hrs.' },
    { range: '1.70 – 1.99', max: '12 Cr. Hrs.' }
  ];

  const workloadScience = [
    { range: '3.75 – 4.00', max: '28 Cr. Hrs.' },
    { range: '3.50 – 3.74', max: '26 Cr. Hrs.' },
    { range: '2.75 – 3.49', max: '24 Cr. Hrs.' },
    { range: '2.25 – 2.74', max: '22 Cr. Hrs.' },
    { range: '2.00 – 2.24', max: '20 Cr. Hrs.' },
    { range: '1.70 – 1.99', max: '15 Cr. Hrs.' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#6BCCF2] to-teal-50 text-[#014864] py-12">
      <div className="max-w-6xl mx-auto px-6">
        {/* Hero */}
        <motion.header initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#6BCCF2] to-teal-50 p-8 shadow-xl">
          <div className="flex items-center gap-6">
            <div className="p-4 rounded-full bg-white/80 shadow-md">
              <BookOpen className="w-12 h-12 text-[#014864]" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-[#014864]">Academic System</h1>
              <p className="mt-2 text-[#014864] max-w-2xl">The Credit Hour System at UCTC follows the Open Credit Hour System (OCHS). Students must complete the stipulated credit hours to graduate. Below you will find the grading system, evaluation scheme, workload policies, and grade improvement rules.</p>
              <div className="mt-4 flex gap-3">
                <a href="/mnt/data/Academic-System.docx" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#014864] text-white font-semibold shadow">Download Doc</a>
                <button onClick={() => setShowMore((s) => !s)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-200 text-amber-800">{showMore ? 'Hide Details' : 'Quick Summary'}</button>
              </div>
            </div>

            {/* decorative SVG leaf */}
            <svg className="absolute right-6 top-6 w-40 opacity-30" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <path d="M42.2,-54.6C57.1,-49.2,72.6,-41.6,78.5,-29.1C84.5,-16.5,80.8,0.1,72.6,13.9C64.5,27.8,51.9,38.8,38.4,47.8C24.9,56.9,10.5,64.1,-3.1,67.6C-16.7,71.1,-33.4,70,-44.2,61.6C-55.1,53.2,-60.1,37.4,-63.2,22.9C-66.3,8.4,-67.4,-5.8,-63.9,-19.9C-60.4,-34,-52.3,-48,-38.6,-54.6C-24.9,-61.2,-12.4,-60.5,1.6,-62.3C15.6,-64.1,31.2,-68,42.2,-54.6Z" fill="#fff7ed" transform="translate(100 100)" />
            </svg>
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mt-6">
            {showMore && (
              <div className="rounded-xl bg-white p-4 border border-amber-100 shadow-sm">
                <p className="text-sm text-[#014864]">Minimum workload for a regular semester is 12 credit hours and maximum is 24 credit hours. Under OCHS a student may register credits with advisor approval; workload must align with GPA policies.</p>
              </div>
            )}
          </motion.div>
        </motion.header>

        {/* Grading Table & Evaluation */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="bg-white rounded-2xl p-6 shadow border border-amber-100">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-amber-800">Grading System</h2>
              <BarChart2 className="w-6 h-6 text-[#014864]" />
            </div>

            <div className="mt-4 overflow-auto">
              <table className="w-full text-sm table-auto">
                <thead>
                  <tr className="bg-[#E6FAFE]">
                    <th className="p-2 text-left">SL</th>
                    <th className="p-2 text-left">Marks (%)</th>
                    <th className="p-2 text-left">Letter Grade</th>
                    <th className="p-2 text-left">Grade Points</th>
                    <th className="p-2 text-left">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {grading.map((g) => (
                    <tr key={g.sl} className="border-b hover:bg-[#E6FAFE]">
                      <td className="p-2 align-top">{g.sl}</td>
                      <td className="p-2 align-top">{g.marks}</td>
                      <td className="p-2 align-top font-semibold">{g.grade}</td>
                      <td className="p-2 align-top">{g.gp}</td>
                      <td className="p-2 align-top">{g.remark || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 text-sm text-[#014864]">
              <p><strong>GPA:</strong> GPA is computed by dividing total grade points earned by number of credit hours attempted in a semester. CGPA is computed across all semesters.</p>
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="bg-white rounded-2xl p-6 shadow border border-amber-100">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-amber-800">Evaluation Scheme</h2>
              <Layers className="w-6 h-6 text-[#014864]" />
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {evalScheme.map((e) => (
                <div key={e.label} className="p-3 rounded-lg bg-[#E6FAFE]">
                  <div className="text-sm font-semibold text-amber-800">{e.label}</div>
                  <div className="text-sm text-[#014864]">{e.value}</div>
                </div>
              ))}
            </div>

            <div className="mt-4 text-sm text-[#014864]">
              <p>Minimum workload for a regular semester is <strong>12 credit hours</strong> and maximum load is <strong>24 credit hours</strong>. Advisor and departmental approvals are required for exceptions.</p>
            </div>
          </motion.section>
        </div>

        {/* Workload Tables */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-6 shadow border border-amber-100">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-amber-800">Advisable Workload — Arts & Business</h3>
              <div className="text-sm text-amber-600">Based on GPA</div>
            </div>
            <div className="mt-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#E6FAFE]"><th className="p-2 text-left">GPA Range</th><th className="p-2 text-left">Max Load</th></tr>
                </thead>
                <tbody>
                  {workloadArts.map((w) => (
                    <tr key={w.range} className="border-b hover:bg-[#E6FAFE]"><td className="p-2">{w.range}</td><td className="p-2 font-semibold">{w.max}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-6 shadow border border-amber-100">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-amber-800">Advisable Workload — Science & Engineering</h3>
              <div className="text-sm text-amber-600">Based on GPA</div>
            </div>
            <div className="mt-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#E6FAFE]"><th className="p-2 text-left">GPA Range</th><th className="p-2 text-left">Max Load</th></tr>
                </thead>
                <tbody>
                  {workloadScience.map((w) => (
                    <tr key={w.range} className="border-b hover:bg-[#E6FAFE]"><td className="p-2">{w.range}</td><td className="p-2 font-semibold">{w.max}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>

        {/* Improvement Policy */}
        <motion.section initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-6 bg-white rounded-2xl p-6 shadow border border-amber-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-amber-800">Policy for Improvement of Grade</h3>
              <p className="mt-2 text-sm text-[#014864]">Key points about grade improvement and retake policy.</p>
            </div>
            <RefreshCcw className="w-6 h-6 text-[#014864]" />
          </div>

          <div className="mt-4 grid gap-2 text-sm text-[#014864]">
            <div><strong>Allowed Range:</strong> B- or below, up to pass grade</div>
            <div><strong>Validity:</strong> Within two consecutive semesters</div>
            <div><strong>Payment:</strong> Full tuition fee for the course(s) on credit-hour basis</div>
            <div><strong>Computation:</strong> Highest grade between two examinations will be shown on transcript and marked as RI (Retake for Improvement)</div>
            <div><strong>Maximum per semester:</strong> Two courses</div>
            <div><strong>Load:</strong> Improvement courses will not be counted within normal semester load</div>
            <div><strong>Exclusions:</strong> Graduating students whose result is recommended for graduation by the CBE shall not be eligible</div>
            <div><strong>One-time:</strong> Improvement for a particular course is allowed once only</div>
          </div>
        </motion.section>

        <footer className="mt-10 text-center text-sm text-amber-600">Document source: Academic-System.docx. For full policy open the downloaded document.</footer>
      </div>
    </div>
  );
}
